import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { JSONContent } from "@tiptap/react";
import { z } from "zod";

import {
  ErrorText,
  LoadingIndicator,
  RichTextEditorInputWithMention,
  Row,
} from "@/components";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import { useUpdateCompanyComment } from "@/hooks/mutations/useUpdateCompanyComment";
import { useSetCompanyComment } from "@/hooks/sets/useSetCompanyComment";
import { tiptapDefaultJson } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

const updateCompanyCommentSchema = z.object({
  content: z
    .object({
      type: z.literal("doc"),
      content: z.array(z.any()),
    })
    .refine(
      (data) => {
        return (
          JSON.stringify(data.content) !==
          JSON.stringify(tiptapDefaultJson.content)
        );
      },
      {
        message: "Required",
      },
    ),
});

export const UpdateCompanyCommentForm = ({
  companyCommentId,
  content,
  onCancel,
  onSuccess,
}: {
  companyCommentId: number;
  content: JSONContent;
  onCancel: () => void;
  onSuccess: () => void;
}) => {
  const {
    mutate: updateCompanyComment,
    isPending: isUpdateCompanyCommentPending,
    isError: isUpdateCompanyCommentError,
  } = useUpdateCompanyComment();
  const setCompanyComment = useSetCompanyComment();

  const form = useForm<z.infer<typeof updateCompanyCommentSchema>>({
    defaultValues: {
      // TODO: See if we can get away with not having to cast to this.
      content: content as { type: "doc"; content: unknown[] },
    },
    resolver: zodResolver(updateCompanyCommentSchema),
  });

  function onSubmit(data: z.infer<typeof updateCompanyCommentSchema>) {
    mixpanelUtils.trackButtonClick("Update comment", MIXPANEL_SCREENS.COMPANY);
    updateCompanyComment(
      {
        companyCommentId,
        updateCompanyCommentRequest: { content: data.content },
      },
      {
        onSuccess: (updatedCompanyComment) => {
          setCompanyComment({
            companyCommentToUpdate: updatedCompanyComment,
          });
          onSuccess();
        },
      },
    );
  }

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-2"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <RichTextEditorInputWithMention
                  onChange={field.onChange}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Row className="items-center justify-end gap-2">
          <Button
            className="rounded-full"
            disabled={isUpdateCompanyCommentPending}
            onClick={onCancel}
            type="button"
            variant="ghost"
          >
            Cancel
          </Button>
          <Button
            className="relative rounded-full"
            disabled={isUpdateCompanyCommentPending}
            type="submit"
            variant="outline"
          >
            <span className={isUpdateCompanyCommentPending ? "invisible" : ""}>
              Update
            </span>
            {isUpdateCompanyCommentPending && (
              <LoadingIndicator
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                variant="small"
              />
            )}
          </Button>
        </Row>
        {isUpdateCompanyCommentError && (
          <Row className="justify-end">
            <ErrorText errorMessage="An error occurred while updating comment. Please try again." />
          </Row>
        )}
      </form>
    </Form>
  );
};
